import { useAddBetaUser, useGetBetaUsers, useRemoveBetaUser, useSearchUsers } from '@/services/betaUsers';
import { BetaUser } from '@/types/betaUsers';
import { formatDateToShortString } from '@/utils/calendar';
import rem from '@/utils/rem';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';

interface BetaUsersManagementProps {
  onBack: () => void;
}

const BetaUsersManagement = ({ onBack }: BetaUsersManagementProps) => {
  const [userSearchQuery, setUserSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 5;

  const toast = useToast();

  const {
    data: betaUsers = [],
    isLoading: isLoadingBetaUsers,
    error: betaUsersError,
  } = useGetBetaUsers({
    staleTime: 30000,
  });

  const { data: searchResults = [], isLoading: isSearching } = useSearchUsers(debouncedSearchQuery, {
    enabled: debouncedSearchQuery.length >= 3,
  });

  const addBetaUserMutation = useAddBetaUser();
  const removeBetaUserMutation = useRemoveBetaUser();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(userSearchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [userSearchQuery]);

  const handleAddBetaUser = async (userId: string, userEmail: string) => {
    try {
      await addBetaUserMutation.mutateAsync(userId);
      toast({
        title: 'Beta user added',
        description: `${userEmail} has been added as a beta user`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      // Clear search and reset to first page
      setUserSearchQuery('');
      setCurrentPage(1);
    } catch (error) {
      toast({
        title: 'Failed to add beta user',
        description: error instanceof Error ? error.message : 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleRemoveBetaUser = async (userId: string, userEmail: string) => {
    try {
      await removeBetaUserMutation.mutateAsync(userId);
      toast({
        title: 'Beta user removed',
        description: `${userEmail} has been removed from beta users`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Adjust current page if we removed the last item on the current page
      const newTotalUsers = betaUsers.length - 1;
      const maxPage = Math.ceil(newTotalUsers / pageSize);
      if (currentPage > maxPage && maxPage > 0) {
        setCurrentPage(maxPage);
      }
    } catch (error) {
      toast({
        title: 'Failed to remove beta user',
        description: error instanceof Error ? error.message : 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Card marginBottom={rem(48)} background={bgColor} borderColor={borderColor} boxShadow="md">
      <CardHeader>
        <Flex alignItems="center" justifyContent="space-between">
          <Box>
            <Heading size="md">Beta Users Management</Heading>
            <Text marginTop={1} color="gray.500" fontSize="sm">
              Manage beta user access for testing new features
            </Text>
          </Box>
          <Button colorScheme="gray" onClick={onBack} size="sm">
            Back to Feature Flags
          </Button>
        </Flex>
      </CardHeader>
      <CardBody>
        {/* Search Section */}
        <Box marginBottom={8}>
          <Heading marginBottom={4} size="sm">
            Add Beta User
          </Heading>
          <FormControl>
            <FormLabel fontSize="sm">Search users by email address</FormLabel>
            <Input
              onChange={(e) => setUserSearchQuery(e.target.value)}
              placeholder="Type to search users..."
              size="md"
              value={userSearchQuery}
            />
          </FormControl>

          {/* Search Results */}
          {userSearchQuery.length >= 3 && (
            <Box marginTop={4}>
              {isSearching ? (
                <Flex alignItems="center" justifyContent="center" padding={4}>
                  <Spinner size="md" />
                </Flex>
              ) : searchResults.length === 0 ? (
                <Text color="gray.500" textAlign="center" paddingY={4}>
                  No users found matching "{userSearchQuery}"
                </Text>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {searchResults.map((user: BetaUser) => {
                    return (
                      <Flex
                        key={user.id}
                        alignItems="center"
                        justifyContent="space-between"
                        padding={3}
                        border="1px solid"
                        borderColor={borderColor}
                        borderRadius="md"
                      >
                        <Box>
                          <Text fontWeight="medium">{`${user.firstName} ${user.lastName}`.trim() || 'No name'}</Text>
                          <Text color="gray.500" fontSize="sm">
                            {user.email}
                          </Text>
                        </Box>
                        <HStack spacing={2}>
                          <Button
                            colorScheme="green"
                            isDisabled={addBetaUserMutation.isLoading}
                            onClick={() => handleAddBetaUser(user.id, user.email)}
                            size="sm"
                          >
                            Add as Beta User
                          </Button>
                        </HStack>
                      </Flex>
                    );
                  })}
                </VStack>
              )}
            </Box>
          )}
        </Box>

        {/* Current Beta Users List */}
        <Box>
          <Heading marginBottom={4} size="sm">
            Current Beta Users ({betaUsers.length})
          </Heading>
          {isLoadingBetaUsers ? (
            <Flex alignItems="center" justifyContent="center" padding={8}>
              <Spinner size="xl" />
            </Flex>
          ) : betaUsersError ? (
            <Text color="red.500" textAlign="center" paddingY={4}>
              Error loading beta users: {betaUsersError.message}
            </Text>
          ) : betaUsers.length === 0 ? (
            <Text color="gray.500" textAlign="center" paddingY={4}>
              No beta users configured yet
            </Text>
          ) : (
            <>
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Date Created</Th>
                      <Th width="150px">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {betaUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((user: BetaUser) => (
                      <Tr key={user.id}>
                        <Td fontWeight="medium">
                          {user.fullName || `${user.firstName} ${user.lastName}`.trim() || 'No name'}
                        </Td>
                        <Td>{user.email}</Td>
                        <Td>{formatDateToShortString(user.dateJoined)}</Td>
                        <Td>
                          <Button
                            colorScheme="red"
                            isDisabled={removeBetaUserMutation.isLoading}
                            onClick={() => handleRemoveBetaUser(user.id, user.email)}
                            size="sm"
                            variant="outline"
                          >
                            Remove
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {/* Pagination Controls */}
              {betaUsers.length > pageSize && (
                <Flex alignItems="center" justifyContent="space-between" marginTop={4}>
                  <Text color="gray.500" fontSize="sm">
                    Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, betaUsers.length)}{' '}
                    of {betaUsers.length} users
                  </Text>
                  <HStack spacing={2}>
                    <Button
                      isDisabled={currentPage === 1}
                      leftIcon={<HiChevronLeft />}
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      size="sm"
                      variant="outline"
                    >
                      Previous
                    </Button>
                    <Text fontSize="sm">
                      Page {currentPage} of {Math.ceil(betaUsers.length / pageSize)}
                    </Text>
                    <Button
                      isDisabled={currentPage >= Math.ceil(betaUsers.length / pageSize)}
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(Math.ceil(betaUsers.length / pageSize), prev + 1))
                      }
                      rightIcon={<HiChevronRight />}
                      size="sm"
                      variant="outline"
                    >
                      Next
                    </Button>
                  </HStack>
                </Flex>
              )}
            </>
          )}
        </Box>
      </CardBody>
    </Card>
  );
};

export default BetaUsersManagement;
